import { useState } from 'react';
import { billingErrorMessage, openPortal } from '../../features/billing/billingApi';
import { useBilling } from '../../features/billing/useBilling';
import { useAccount } from '../../features/onboarding/useAccount';
import { usePlan } from './usePlan';

/**
 * One stamp-red line under the header while the owner's Paddle renewal is
 * failing (`past_due`): the plan stays while Paddle retries, and the way out
 * is a new card in Paddle's portal. Read only for an owner on a Paddle plan
 * with billing on; nothing otherwise.
 */
export function PastDueBanner() {
  const { entitlements } = usePlan();
  const { business } = useAccount();
  const { subscription } = useBilling(
    business?.role === 'owner' && entitlements?.plan_source === 'paddle',
  );
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (subscription?.status !== 'past_due') return null;

  async function managePayment() {
    setIsOpening(true);
    setError(null);
    try {
      await openPortal();
    } catch (caught) {
      setError(billingErrorMessage(caught, 'The billing portal did not open. Try again.'));
    } finally {
      setIsOpening(false);
    }
  }

  return (
    <p className="past-due-banner" role="alert">
      <span>Payment failed. Update your card.</span>
      <span aria-hidden="true">·</span>
      <button
        className="past-due-banner__link"
        type="button"
        disabled={isOpening}
        onClick={() => void managePayment()}
      >
        {isOpening ? 'Opening' : 'Manage payment'}
      </button>
      {error ? <span>{error}</span> : null}
    </p>
  );
}
